/**
 * ============================================================================
 * VINPOP — Discover/Taste-Tunner/Playlist Tasting Recommendations
 * ============================================================================
 *
 * Handles similarity recommendations for the 3 "mode tasting" collections:
 *   - discover-mode-tasting
 *   - taste-tunner-tasting
 *   - my-playlist-tasting
 *
 * Flow (triggered when ?source_product=ID is in URL):
 *   1. Read source Shopify product ID from URL
 *   2. Look up its `productidmatch` metafield = real API wine ID
 *   3. Call api.vinpop.fr/api/wines/getDistance with that wine ID
 *   4. Map API results → Shopify products via `productidmatch` catalog
 *   5. Display products sorted by score with % match badges
 *
 * Click redirection (product → collection?source_product=ID) is already
 * handled by snippets/product-tile.liquid — no interceptor needed here.
 *
 * Depends on: discover-tasting-catalog-data.liquid (window.discoverTastingCatalog)
 * Loaded from: layout/theme.liquid (conditional on collection handle)
 * ============================================================================
 */

(function () {
  'use strict';

  if (window.discoverTastingRecommendationsLoaded) return;
  window.discoverTastingRecommendationsLoaded = true;

  const HANDLED_COLLECTIONS = [
    'discover-mode-tasting',
    'taste-tunner-tasting',
    'my-playlist-tasting'
  ];

  const pathname = window.location.pathname;
  const isHandledCollection = HANDLED_COLLECTIONS.some(h => pathname.includes('/collections/' + h));

  if (!isHandledCollection) return;

  console.log('🍷 Discover-Tasting Recommendations: Initializing on', pathname);

  // ---------------------------------------------------------------------------
  // HELPERS
  // ---------------------------------------------------------------------------

  function isNL() {
    return pathname.startsWith('/nl') ||
      (!pathname.startsWith('/en') && (document.documentElement.lang || '').toLowerCase().startsWith('nl'));
  }

  // Resolve the variant ID of the real wine for a discover-tasting product.
  // Mirrors the logic from vinpop-favourites.js resolveLoveTargetProductIds()
  // Uses productidmatch as the wine API ID.
  function resolveDiscoverRealWineVariant(cardProductId, callback) {
    var id = String(cardProductId || '');
    if (!id) { callback(null); return; }

    var catalogEntry = (window.discoverTastingCatalog || {})[id];
    if (!catalogEntry) { callback(null); return; }

    // For discover-tasting, use productidmatch (API wine ID) as matchKey
    var matchKey = String(catalogEntry.productidmatch || '');
    if (!matchKey) { callback(null); return; }

    console.log('[resolveDiscoverRealWineVariant]', { cardProductId: id, productidmatch: matchKey });

    var shopCatalog = window.shopCatalog || {};
    var shopCatalogIndex = window.shopCatalogIndex || {};
    var candidates = [];

    // 1) Search shopCatalog by relatedProductId === matchKey (prefer price > 0)
    for (var k1 in shopCatalog) {
      var entry1 = shopCatalog[k1] || {};
      if (String(entry1.relatedProductId || '') !== matchKey) continue;
      if (String(k1) === id) continue; // skip the product itself

      if (!candidates.length && Number(entry1.price || 0) > 0) {
        var vars1 = entry1.variants || [];
        var av1 = vars1.filter(function(v) { return v.available; })[0] || vars1[0];
        if (av1) {
          console.log('[resolveDiscoverRealWineVariant] Found in shopCatalog (price > 0):', k1, 'variant:', av1.id);
          callback(String(av1.id));
          return;
        }
      }
      candidates.push(k1);
    }

    // 1b) If no priced product found, try first candidate
    if (candidates.length) {
      var firstCandidate = shopCatalog[candidates[0]];
      if (firstCandidate && firstCandidate.variants && firstCandidate.variants.length) {
        var avCand = firstCandidate.variants.filter(function(v) { return v.available; })[0] || firstCandidate.variants[0];
        if (avCand) {
          console.log('[resolveDiscoverRealWineVariant] Found in shopCatalog (any):', candidates[0], 'variant:', avCand.id);
          callback(String(avCand.id));
          return;
        }
      }
    }

    // 2) Search shopCatalogIndex by r (relatedProductId) === matchKey, then fetch handle
    for (var k2 in shopCatalogIndex) {
      if (k2 === '_') continue;
      var entry2 = shopCatalogIndex[k2] || {};
      if (String(entry2.r || '') !== matchKey) continue;
      if (String(k2) === id) continue;

      var handle2 = entry2.h;
      if (handle2) {
        console.log('[resolveDiscoverRealWineVariant] Found in shopCatalogIndex:', k2, 'handle:', handle2, 'fetching...');
        fetch('/products/' + handle2 + '.js')
          .then(function(r) { return r.json(); })
          .then(function(p) {
            var av2 = (p.variants || []).filter(function(v) { return v.available; })[0] || (p.variants || [])[0];
            if (av2) {
              console.log('[resolveDiscoverRealWineVariant] Fetched variant:', av2.id);
              callback(String(av2.id));
            } else {
              console.log('[resolveDiscoverRealWineVariant] No variants in fetched product');
              callback(null);
            }
          })
          .catch(function(err) {
            console.error('[resolveDiscoverRealWineVariant] Fetch error:', err);
            callback(null);
          });
        return;
      }
    }

    // 3) Fallback: if matchKey itself is a Shopify product ID in catalog
    if (shopCatalog[matchKey] && shopCatalog[matchKey].variants) {
      var avFb = shopCatalog[matchKey].variants.filter(function(v) { return v.available; })[0] || shopCatalog[matchKey].variants[0];
      if (avFb) {
        console.log('[resolveDiscoverRealWineVariant] Using matchKey as product ID:', matchKey, 'variant:', avFb.id);
        callback(String(avFb.id));
        return;
      }
    }

    if (shopCatalogIndex[matchKey] && shopCatalogIndex[matchKey].h) {
      var handleFb = shopCatalogIndex[matchKey].h;
      console.log('[resolveDiscoverRealWineVariant] matchKey in index, fetching handle:', handleFb);
      fetch('/products/' + handleFb + '.js')
        .then(function(r) { return r.json(); })
        .then(function(p) {
          var avFb = (p.variants || []).filter(function(v) { return v.available; })[0] || (p.variants || [])[0];
          if (avFb) {
            console.log('[resolveDiscoverRealWineVariant] Fetched from matchKey handle:', avFb.id);
            callback(String(avFb.id));
          } else {
            console.log('[resolveDiscoverRealWineVariant] No variants in matchKey product');
            callback(null);
          }
        })
        .catch(function(err) {
          console.error('[resolveDiscoverRealWineVariant] Fallback fetch error:', err);
          callback(null);
        });
      return;
    }

    console.log('[resolveDiscoverRealWineVariant] No resolution path found');
    callback(null);
  }

  function addDiscoverCartButton(card) {
    var quickBuy = card.querySelector('.product-tile__quick-buy');
    if (!quickBuy) return;

    var cardProductId = (card.querySelector('.product-tile[data-product-id]') || card).getAttribute('data-product-id');
    if (!cardProductId) return;

    // Only add buttons if this card has catalog data
    var catalogEntry = (window.discoverTastingCatalog || {})[String(cardProductId)];
    if (!catalogEntry) return;

    // Idempotent
    quickBuy.querySelector('.vinpop-discover-see-wrapper') && quickBuy.querySelector('.vinpop-discover-see-wrapper').remove();
    quickBuy.querySelector('.vinpop-discover-cart-wrapper') && quickBuy.querySelector('.vinpop-discover-cart-wrapper').remove();

    // --- "See the real product" link button ---
    var seeWrapper = null;
    var realHandle = resolveRealWineHandle(cardProductId);
    if (realHandle) {
      var realUrl = '/products/' + realHandle;
      var localePrefix = pathname.match(/^\/(nl|en)\//); 
      if (localePrefix) realUrl = '/' + localePrefix[1] + realUrl;

      seeWrapper = document.createElement('div');
      seeWrapper.className = 'vinpop-fav-btn-wrapper vinpop-discover-see-wrapper';
      var seeLink = document.createElement('a');
      seeLink.href = realUrl;
      seeLink.className = 'vinpop-tasting-cart-btn';
      seeLink.style.cssText = 'display:block;text-align:center;text-decoration:none;';
      seeLink.innerHTML = '🍷 See the real product';
      seeWrapper.appendChild(seeLink);
    }

    // Append the "see" wrapper after the fav button
    function appendSeeAfterFav() {
      if (seeWrapper) quickBuy.appendChild(seeWrapper);
    }

    var existingFav = quickBuy.querySelector('.vinpop-fav-btn-wrapper:not(.vinpop-discover-see-wrapper):not(.vinpop-discover-cart-wrapper)');
    if (existingFav) {
      appendSeeAfterFav();
    } else {
      var obs = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1 &&
                node.classList.contains('vinpop-fav-btn-wrapper') &&
                !node.classList.contains('vinpop-discover-see-wrapper') &&
                !node.classList.contains('vinpop-discover-cart-wrapper')) {
              appendSeeAfterFav();
              obs.disconnect();
            }
          });
        });
      });
      obs.observe(quickBuy, { childList: true });
    }
  }

  // Resolve the URL (handle) of the real wine for a discover-tasting proxy product.
  // Returns the URL string or null if not found (synchronous only).
  function resolveRealWineHandle(cardProductId) {
    var id = String(cardProductId || '');
    var catalogEntry = (window.discoverTastingCatalog || {})[id];
    if (!catalogEntry) return null;

    var matchKey = String(catalogEntry.productidmatch || '');
    if (!matchKey) return null;

    var shopCatalog = window.shopCatalog || {};
    var shopCatalogIndex = window.shopCatalogIndex || {};

    // Search shopCatalog by relatedProductId === matchKey (prefer price > 0)
    var fallbackHandle = null;
    for (var k in shopCatalog) {
      var entry = shopCatalog[k] || {};
      if (String(entry.relatedProductId || '') !== matchKey) continue;
      if (String(k) === id) continue;
      if (!fallbackHandle) fallbackHandle = entry.handle || null;
      if (Number(entry.price || 0) > 0 && entry.handle) {
        return entry.handle;
      }
    }
    if (fallbackHandle) return fallbackHandle;

    // Search shopCatalogIndex by r === matchKey
    for (var k2 in shopCatalogIndex) {
      if (k2 === '_') continue;
      var entry2 = shopCatalogIndex[k2] || {};
      if (String(entry2.r || '') !== matchKey) continue;
      if (String(k2) === id) continue;
      if (entry2.h) return entry2.h;
    }

    // matchKey itself may be a Shopify product ID
    if (shopCatalog[matchKey] && shopCatalog[matchKey].handle) return shopCatalog[matchKey].handle;
    if (shopCatalogIndex[matchKey] && shopCatalogIndex[matchKey].h) return shopCatalogIndex[matchKey].h;

    return null;
  }

  // When ?source_product is in the URL, redirect all tile links to the real product page.
  function redirectCardLinksToRealProduct(card, cardProductId) {
    var handle = resolveRealWineHandle(cardProductId);
    if (!handle) return;

    var realUrl = '/products/' + handle;
    // Prepend locale prefix if needed
    var prefix = pathname.match(/^\/(nl|en)\//);
    if (prefix) realUrl = '/' + prefix[1] + realUrl;

    card.querySelectorAll('a').forEach(function(a) {
      var href = a.getAttribute('href') || '';
      // Only redirect links that point to the collection (source_product links) or to /products/
      if (href.includes('/collections/discover-mode-tasting') || href.includes('/products/')) {
        a.setAttribute('href', realUrl);
      }
    });
  }

  function addScoreBadge(card, score, isSource) {
    // Remove any pre-existing badge
    card.querySelectorAll('.vinpop-score-badge, .vinpop-dt-badge').forEach(b => b.remove());

    // Anchor on the card itself (li.product-tile container), not on image.
    // Find the inner tile container so we don't get clipped by image overflow.
    const tile = card.querySelector('.product-tile__container') || card.querySelector('.product-tile') || card;
    tile.style.position = 'relative';
    // Ensure badge is not clipped by any overflow:hidden on parents
    tile.style.overflow = 'visible';
    if (card.style) card.style.overflow = 'visible';

    const nl = isNL();
    const isSelectedProduct = isSource || score === 100;

    const badgeInner = isSelectedProduct
      ? `<span style="font-size:14px;font-weight:800;line-height:1.1;text-align:center;">${nl ? 'Gekozen' : 'Selected'}</span>`
      : `<div style="line-height:1.05;text-align:center;"><span style="font-size:22px;font-weight:900;">${score}%</span><br><span style="font-size:10px;text-transform:uppercase;letter-spacing:0.05em;font-weight:700;">match</span></div>`;

    const badgeHTML = `
      <div class="vinpop-dt-badge vinpop-score-badge"
           style="position:absolute;top:8px;right:8px;width:72px;height:72px;border-radius:50%;
                  background:#E07A5F;color:#fff;display:flex;align-items:center;justify-content:center;
                  text-align:center;font-family:'Prompt',sans-serif;
                  box-shadow:0 3px 10px rgba(0,0,0,0.18);
                  transform:rotate(5deg);
                  z-index:30;pointer-events:none;
                  border:2px solid rgba(0,0,0,0.15);">
        ${badgeInner}
      </div>`;

    tile.insertAdjacentHTML('beforeend', badgeHTML);
  }

  function addJustificationText(card, score, isSource, sourceName) {
    card.querySelectorAll('.vinpop-quiz-justification').forEach(j => j.remove());

    const nl = isNL();
    let text;

    if (isSource) {
      text = nl ? '🍷 Geselecteerde wijn' : '🍷 Selected wine';
    } else if (score >= 95) {
      text = nl
        ? `🎯 ${score}% match${sourceName ? ' met ' + sourceName : ''} — alleen professionele proevers merken het verschil`
        : `🎯 ${score}% match${sourceName ? ' with ' + sourceName : ''} — only professional tasters perceive the difference`;
    } else if (score >= 91) {
      text = nl
        ? `🎯 ${score}% match${sourceName ? ' met ' + sourceName : ''} — ook wijnkenners merken het verschil`
        : `🎯 ${score}% match${sourceName ? ' with ' + sourceName : ''} — wine connoisseurs also perceive the difference`;
    } else if (score >= 85) {
      text = nl
        ? `🎯 ${score}% match${sourceName ? ' met ' + sourceName : ''} — zeer dicht smaakprofiel`
        : `🎯 ${score}% match${sourceName ? ' with ' + sourceName : ''} — very close taste profile`;
    } else if (score >= 80) {
      text = nl
        ? `🎯 ${score}% match${sourceName ? ' met ' + sourceName : ''} — verwante wijn`
        : `🎯 ${score}% match${sourceName ? ' with ' + sourceName : ''} — related wine`;
    } else {
      text = nl
        ? `🎯 ${score}% match — andere smaak`
        : `🎯 ${score}% match — different taste`;
    }

    const justifEl = document.createElement('div');
    justifEl.className = 'vinpop-quiz-justification';
    justifEl.innerHTML = `<span class="vinpop-justif-line">${text}</span>`;

    const priceElement = card.querySelector('[data-product-price]');
    const textLink = card.querySelector('.product-tile.mt-5 > a.block, .product-tile.mt-5 > a');

    if (textLink && priceElement) {
      priceElement.insertAdjacentElement('afterend', justifEl);
    } else if (textLink) {
      textLink.appendChild(justifEl);
    } else {
      const textArea = card.querySelector('.product-tile.mt-5, .product-tile');
      if (textArea) textArea.appendChild(justifEl);
    }
  }

  function injectStyles() {
    if (document.getElementById('discover-tasting-css')) return;
    const style = document.createElement('style');
    style.id = 'discover-tasting-css';
    style.textContent = `
      .dt-hidden { display: none !important; }
      /* Self-maintaining rule: hide any card that has no badge (survives Alpine.js re-renders) */
      #facets-results li[data-product-id]:not(:has(.vinpop-dt-badge)) { display: none !important; }
      .vinpop-quiz-justification {
        margin-top: 0.5rem;
        padding: 0.4rem 0.6rem;
        background: rgba(var(--color-scheme-accent-2), 0.1);
        border-radius: 6px;
        font-size: 0.75rem;
        line-height: 1.3;
        color: rgb(var(--color-scheme-text));
      }
      .vinpop-justif-line { display: block; }
    `;

    // Discover mode: hide all prices on product cards
    if (pathname.includes('/collections/discover-mode-tasting')) {
      style.textContent += `
        #facets-results [data-product-price],
        #facets-results .price,
        #facets-results .price__container,
        #facets-results .price-item {
          display: none !important;
        }
      `;
    }

    document.head.appendChild(style);
  }

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  function renderResults(matchedProducts, sourceShopifyId) {
    const grid = document.querySelector('#facets-results ul');
    if (!grid) {
      console.log('⏳ Discover-Tasting: Grid not found, retrying...');
      setTimeout(() => renderResults(matchedProducts, sourceShopifyId), 200);
      return;
    }

    injectStyles();

    const allCards = Array.from(grid.querySelectorAll('li[data-product-id]'));
    const matchedIds = new Set(matchedProducts.map(m => String(m.shopifyId)));

    // Build card map
    const cardMap = {};
    allCards.forEach(card => {
      const id = card.getAttribute('data-product-id');
      if (id) cardMap[id] = card;
    });

    // Hide cards with no match — inline style survives Alpine.js re-renders
    allCards.forEach(card => {
      const id = card.getAttribute('data-product-id');
      if (!matchedIds.has(id)) {
        card.classList.add('dt-hidden');
        card.style.display = 'none';
      }
    });

    const sourceProduct = matchedProducts.find(m => m.isSource);
    const sourceName = sourceProduct
      ? (window.discoverTastingCatalog[String(sourceProduct.shopifyId)] || {}).title || null
      : null;

    let shownCount = 0;
    matchedProducts.forEach((item, index) => {
      const card = cardMap[String(item.shopifyId)];
      if (!card) {
        console.warn('⚠️ Discover-Tasting: card not found for Shopify ID', item.shopifyId);
        return;
      }

      card.classList.remove('dt-hidden');
      card.style.display = '';
      card.style.order = String(index);

      addScoreBadge(card, item.score, item.isSource);
      addJustificationText(card, item.score, item.isSource, sourceName);
      addDiscoverCartButton(card);

      shownCount++;
    });

    console.log(`✅ Discover-Tasting: Rendered ${shownCount} products with badges`);
  }

  // ---------------------------------------------------------------------------
  // MAIN — fetch API and compute scores
  // ---------------------------------------------------------------------------

  async function displaySimilarProducts() {
    const urlParams = new URLSearchParams(window.location.search);
    const sourceProductId = urlParams.get('source_product');
    if (!sourceProductId) return;

    console.log('🍷 Discover-Tasting: source_product =', sourceProductId);

    // Inject styles early so the :has() CSS rule is in place before grid renders
    injectStyles();

    // Wait for catalog
    if (!window.discoverTastingCatalog || !window.discoverTastingByWineId) {
      console.log('⏳ Discover-Tasting: waiting for catalog...');
      setTimeout(displaySimilarProducts, 200);
      return;
    }

    const sourceProduct = window.discoverTastingCatalog[String(sourceProductId)];
    if (!sourceProduct) {
      console.error('❌ Discover-Tasting: source product not found in catalog. ID:', sourceProductId);
      return;
    }

    const realWineId = sourceProduct.productidmatch;
    if (!realWineId) {
      console.error('❌ Discover-Tasting: no productidmatch for product:', sourceProduct.title);
      console.log('🔎 Debug metafields for source product:', sourceProduct._debug_metafields);
      console.log('🔎 Full source product object:', sourceProduct);
      return;
    }

    console.log('🍷 Discover-Tasting: Source product:', sourceProduct.title, '— API wine ID:', realWineId);

    try {
      const response = await fetch('https://api.vinpop.fr/api/wines/getDistance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: String(realWineId) })
      });

      if (!response.ok) throw new Error('API error: ' + response.status);

      const compatibilityData = await response.json();
      console.log('📊 Discover-Tasting: API returned', compatibilityData.length, 'pairs');

      if (!compatibilityData || compatibilityData.length === 0) {
        console.warn('❌ Discover-Tasting: no compatibility data');
        return;
      }

      // Build map: API wine ID → best score
      const wineScoreMap = {};
      compatibilityData.forEach(pair => {
        [pair.productid1, pair.productid2].forEach(wineId => {
          if (String(wineId) === String(realWineId)) return; // skip source

          const distance = pair.perceptive_distance || pair.distance || 0;
          const score = Math.max(0, Math.floor(100 - distance));

          if (!wineScoreMap[wineId] || score > wineScoreMap[wineId]) {
            wineScoreMap[wineId] = score;
          }
        });
      });

      console.log('📊 Discover-Tasting: scored', Object.keys(wineScoreMap).length, 'unique wine IDs');

      // Map API IDs back to Shopify products in THIS collection
      const matchedProducts = [];

      // Always include source product first (score 100)
      matchedProducts.push({
        shopifyId: String(sourceProductId),
        score: 100,
        isSource: true
      });

      Object.keys(wineScoreMap).forEach(wineId => {
        const shopifyProduct = window.discoverTastingByWineId[String(wineId)];
        if (!shopifyProduct) return; // not in this collection

        // Don't add source product twice
        if (String(shopifyProduct.id) === String(sourceProductId)) return;

        matchedProducts.push({
          shopifyId: String(shopifyProduct.id),
          score: wineScoreMap[wineId],
          isSource: false
        });
      });

      // Sort by score descending (source stays first due to score 100)
      matchedProducts.sort((a, b) => b.score - a.score);

      console.log('✅ Discover-Tasting:', matchedProducts.length, 'products matched in this collection');

      // Wait for DOM grid to be populated then render
      waitForGridAndRender(matchedProducts, sourceProductId);

    } catch (err) {
      console.error('❌ Discover-Tasting: API error:', err);
    }
  }

  function waitForGridAndRender(matchedProducts, sourceProductId, attempts) {
    attempts = attempts || 0;
    const grid = document.querySelector('#facets-results ul');
    const cards = grid ? grid.querySelectorAll('li[data-product-id]') : [];

    if (cards.length === 0 && attempts < 25) {
      setTimeout(() => waitForGridAndRender(matchedProducts, sourceProductId, attempts + 1), 300);
      return;
    }

    console.log('✅ Discover-Tasting: Grid ready with', cards.length, 'cards');
    renderResults(matchedProducts, sourceProductId);
  }

  // ---------------------------------------------------------------------------
  // BANNER LEGEND (score scale explanation under the H1)
  // ---------------------------------------------------------------------------

  function injectBannerLegend() {
    if (document.querySelector('.vinpop-dt-legend')) return;

    const urlParams = new URLSearchParams(window.location.search);
    const isDiscoverWithSource = pathname.includes('/collections/discover-mode-tasting') && !!urlParams.get('source_product');

    const h1 = document.querySelector('.shopify-section section h1.heading-feature, .shopify-section section h1');

    const nl = isNL();
    const lines = nl
      ? [
          '≥ 95% : Alleen professionele proevers merken het verschil',
          '91–94% : Wijnkenners merken ook het verschil',
          '85–90% : Zeer vergelijkbaar smaakprofiel',
          '80–84% : Verwante wijn',
          '< 80% : Andere smaak'
        ]
      : [
          '≥ 95% : Only professional tasters perceive the difference',
          '91–94% : Wine connoisseurs also perceive the difference',
          '85–90% : Very close taste profile',
          '80–84% : Related wine',
          '< 80% : Different taste'
        ];

    const legend = document.createElement('div');
    legend.className = 'vinpop-dt-legend mt-5 rte mx-auto max-w-xl text-center text-rte';
    legend.innerHTML = '<p>' + lines.join('<br>') + '</p>';

    // On discover-mode with source_product: replace the helper section content entirely
    // (remove existing text + watch button and keep only legend in that section).
    if (isDiscoverWithSource) {
      const featureText = Array.from(document.querySelectorAll('section .feature-text')).find(el => {
        const txt = (el.textContent || '').toLowerCase();
        return txt.includes('select a bottle below') || txt.includes('watch how it works') || txt.includes('selecteer hieronder');
      });
      if (featureText) {
        featureText.innerHTML = legend.outerHTML;
        return;
      }
    }

    if (!h1) return;

    const headingWrapper = h1.closest('.break-words') || h1.parentElement;
    headingWrapper.appendChild(legend);
  }

  // ---------------------------------------------------------------------------
  // INIT
  // ---------------------------------------------------------------------------

  function init() {
    injectBannerLegend();
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('source_product')) {
      displaySimilarProducts();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 200);
  }

})();
